import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import NEW_API from "../../../Api/NewApi";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import Button from "../../Common/FormElements/Button/Button";
import { IMAGE_END_POINT } from "../../../Api/EndPoints";
import { viewQuotePdf } from "../../../REDUX/actions/appProjects";

// Quote event cards (message.type === "quote") — raw i18n keys.
const QUOTE_EVENT_LABELS = {
  created: "A quote has been created",
  updated: "A quote has been updated",
  sent: "A quote has been sent",
  accepted: "A quote has been accepted",
};
// Scénario B entries (message.type === "event", meta.kind) → icon, colour.
const EVENT_ICONS = {
  quote_accepted: ["bi-check2-circle", "#14748e"],
  quote_accepted_by_advisor: ["bi-check2-circle", "#14748e"],
  quote_refused: ["bi-x-circle", "#666666"],
  quote_expired: ["bi-hourglass-bottom", "#666666"],
  quote_new_requested: ["bi-arrow-repeat", "#c9762e"],
  order_created: ["bi-bag-check", "#14748e"],
  order_creation_failed: ["bi-exclamation-triangle", "#cc1f08"],
  order_cancelled: ["bi-x-octagon", "#cc1f08"],
  order_delivered: ["bi-house-check", "#2e7d32"],
  deposit_received: ["bi-credit-card", "#2e7d32"],
  delivery_date_requested: ["bi-calendar-event", "#14748e"],
  delivery_date_validated: ["bi-calendar-check", "#2e7d32"],
  delivery_date_proposed: ["bi-calendar-x", "#c9762e"],
  delivery_date_customer_response: ["bi-calendar-event", "#14748e"],
};
const formatEuro = (value) =>
  `${(Number(value) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

/**
 * Slide 10 — Discussion: the chat between the app user (main contact) and the
 * organization's staff, reusing the app's conversation/message stack. Live
 * updates arrive on the existing SOFT socket ("message:new" → refetch).
 */
const ProjectDiscussion = ({ project }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const { socket } = useSelector((state) => state.socketReducer) ?? {};
  const myId = user?.id ?? Number(user?.attributes?.id);

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchDiscussion = useCallback(
    async (signal) => {
      try {
        const { data } = await NEW_API.get(
          `/api/projects-discussion/${project.id}`,
          { signal },
        );
        setConversationId(data?.data?.conversationId ?? null);
        setMessages(data?.data?.messages ?? []);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [project.id],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchDiscussion(controller.signal);
    return () => controller.abort();
  }, [fetchDiscussion]);

  // Live updates: join the conversation room and refetch on any new message.
  useEffect(() => {
    if (!socket || !conversationId) return;
    socket.emit("conversation:join", { conversationId });
    const onNewMessage = () => fetchDiscussion();
    socket.on("message:new", onNewMessage);
    socket.on("message:deleted", onNewMessage);
    socket.on("message:edited", onNewMessage);
    return () => {
      socket.off("message:new", onNewMessage);
      socket.off("message:deleted", onNewMessage);
      socket.off("message:edited", onNewMessage);
    };
  }, [socket, conversationId, fetchDiscussion]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const handleSend = async () => {
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    try {
      const { data } = await NEW_API.post(
        `/api/projects-discussion/${project.id}`,
        { data: { text: value } },
      );
      // The socket refetch (message:new) usually lands first — don't add it twice.
      setMessages((prev) =>
        prev.some((m) => m.id === data?.data?.id)
          ? prev
          : [...prev, data?.data],
      );
      setText("");
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    } finally {
      setSending(false);
    }
  };

  const senderName = (sender) =>
    `${sender?.firstName ?? ""} ${sender?.lastName ?? ""}`.trim() ||
    sender?.username ||
    "-";

  return (
    <div className="flex-1 d-flex flex-column px-5 pt-4 pb-4">
      <div
        className="flex-grow-1 overflow-auto customScroll pe-2"
        style={{ maxHeight: 500, minHeight: 300 }}
      >
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner />
          </div>
        ) : messages.length === 0 ? (
          <p className="f14 text-brightGrey text-center py-5">
            {t("No messages yet")}
          </p>
        ) : (
          messages.map((message) => {
            // Scénario B system entries: the text is the French line written
            // by the API; meta.via "app" = done by the customer in the app.
            if (message?.type === "event") {
              const meta = message.meta ?? {};
              const [icon, color] = EVENT_ICONS[meta.kind] ?? [
                "bi-info-circle",
                "#14748e",
              ];
              return (
                <div
                  key={message?.clientId ?? message?.id}
                  className="d-flex justify-content-center mb-3"
                >
                  <div
                    className="px-3 py-2 br10 b-1-lightGrey bg-white"
                    style={{ minWidth: 320, maxWidth: "80%" }}
                  >
                    <div className="d-flex align-items-start gap-2 f14 sBold text-ultraBlack">
                      <i className={`bi ${icon}`} style={{ color }} />
                      <span className="flex-1">{message?.text}</span>
                      {meta.via === "app" && (
                        <span
                          className="f11 sBold px-2 br10 text-nowrap"
                          style={{ color: "#14748e", background: "#e7f2f5" }}
                        >
                          {t("From app")}
                        </span>
                      )}
                    </div>
                    <div className="f12 text-brightGrey mt-1">
                      {moment(message?.createdAt).format("DD/MM/YYYY HH:mm")}
                    </div>
                  </div>
                </div>
              );
            }
            if (message?.type === "quote") {
              const meta = message.meta ?? {};
              return (
                <div
                  key={message?.clientId ?? message?.id}
                  className="d-flex justify-content-center mb-3"
                >
                  <div
                    className="px-3 py-2 br10 b-1-lightGrey bg-white"
                    style={{ minWidth: 320, maxWidth: "80%" }}
                  >
                    <div className="d-flex align-items-center gap-2 f14 sBold text-ultraBlack">
                      <i
                        className="bi bi-file-earmark-text"
                        style={{ color: "#14748e" }}
                      />
                      {t(QUOTE_EVENT_LABELS[meta.event] ?? "Quote")}
                    </div>
                    <div className="f13 text-ultraBlack mt-1">
                      {meta.reference ||
                        `${t("Quote")} n°${meta.quoteId ?? ""}`}
                      {" — "}
                      <span className="sBold" style={{ color: "#14748e" }}>
                        {formatEuro(meta.totalAmount)}
                      </span>
                    </div>
                    {meta.quoteId ? (
                      <button
                        className="btn btn-link p-0 f13 mt-1"
                        style={{ color: "#14748e" }}
                        onClick={() => dispatch(viewQuotePdf(meta.quoteId, t))}
                      >
                        {t("View the quote")}
                      </button>
                    ) : null}
                    <div className="f12 text-brightGrey mt-1">
                      {senderName(message?.sender)} —{" "}
                      {moment(message?.createdAt).format("DD/MM/YYYY HH:mm")}
                    </div>
                  </div>
                </div>
              );
            }
            const mine = message?.sender?.id === myId;
            return (
              <div
                key={message?.clientId ?? message?.id}
                className={`d-flex flex-column mb-3 ${
                  mine ? "align-items-end" : "align-items-start"
                }`}
              >
                <span className="f12 text-brightGrey mb-1">
                  {senderName(message?.sender)} —{" "}
                  {moment(message?.createdAt).format("DD/MM/YYYY HH:mm")}
                </span>
                <div
                  className={`px-3 py-2 br10 f14 ${
                    mine ? "text-white" : "bg-dimGrey text-ultraBlack"
                  }`}
                  style={{
                    maxWidth: "70%",
                    backgroundColor: mine ? "#345d68" : undefined,
                  }}
                >
                  {message?.text}
                  {(message?.attachments ?? []).map((attachment) => (
                    <a
                      key={attachment.id}
                      href={`${IMAGE_END_POINT}${attachment.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className={`d-block f13 underline mt-1 ${
                        mine ? "text-white" : "text-blue"
                      }`}
                    >
                      <i className="bi bi-paperclip me-1" />
                      {attachment.name ?? t("File")}
                    </a>
                  ))}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
      <div className="d-flex align-items-center gap-3 mt-3">
        <div className="flex-grow-1">
          <TextInput
            placeholder={t("Write a message")}
            value={text}
            callback={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
          />
        </div>
        <Button
          loading={sending}
          disabled={!text.trim()}
          Icon={() => <i className="bi bi-send" />}
          onClick={handleSend}
        >
          {t("Send")}
        </Button>
      </div>
    </div>
  );
};

export default ProjectDiscussion;
